/* MONDSTEIN by Metagon · mds-buy.js · buy box, gallery, sticky add-to-cart (sections mds-buy-box, mds-sticky-atc).
   Ported from the mock's site.js §7 / §7a / §9, wired to the real Shopify cart.
   State: one selection per page = offer (data-mds-offer, e.g. "probe" or "partner") + weight + size (option values, e.g.
   "7 kg", "135×200"). Only the exact variant of weight + size is ever added; if it does not exist, the button is disabled.
   The buy box prints its product data as JSON ([data-mds-buy-data]); every element below, wherever it sits, follows it:
     [data-mds-weight] / [data-mds-size]  radio chips (sold-out marks follow the other option)   [data-mds-offer]  offer cards
     [data-mds-price]   current price                             [data-mds-label]   "Probeschlaf-Paket · 7 kg · 135×200 cm"
     [data-mds-sub], [data-mds-box], [data-mds-hint]               [data-weight-ui] / [data-set-ui]  hidden per offer
     [data-mds-unavail] line "Gerade nicht lieferbar: ..."        [data-mds-instock-only]  hidden while the choice is sold out
     [data-mds-atc]     add-to-cart buttons ([data-mds-atc-label] inside; [data-mds-variant-id] = no-JS form id)
     [data-mds-atc-msg] / [data-mds-toast]  status                 radiogroups: arrow keys + roving tabindex
   Cart: POST /cart/add.js with the cart-items section ids, then Horizon's own CartLinesUpdateEvent (@shopify/events), so
   the header cart count, the cart drawer and the cart page update exactly as with Horizon's product form.
   Other sections pick a weight or an offer through the "mds:pick" event (mds-core.js). */
import { $, $$, onScroll, claim, reduce } from '@mds/core';

const S = { data: null, weight: null, size: null, offer: null };

function readData() {
  const el = document.querySelector('[data-mds-buy-data]');
  if (!el) return null;
  try { return JSON.parse(el.textContent); } catch (e) { console.warn('[mds-buy] bad product data', e); return null; }
}

/** the one variant with this weight and size, or null (never another weight or size: review H2) */
function variantFor(weight, size) {
  const d = S.data;
  if (!d || !d.variants.length) return null;
  return d.variants.find((v) => (!d.weightPos || v.o[d.weightPos - 1] === weight) && (!d.sizePos || v.o[d.sizePos - 1] === size)) || null;
}
/** is any variant with option value `val` at `pos` available, together with the other option's current value? */
function availableWith(pos, val) {
  const d = S.data, other = pos === d.weightPos ? d.sizePos : d.weightPos, ov = pos === d.weightPos ? S.size : S.weight;
  return d.variants.some((v) => v.a && v.o[pos - 1] === val && (!other || v.o[other - 1] === ov));
}
// number and unit never part at a line break ("7 kg", "135×200 cm": no-break space U+00A0; review 16-10-26)
const sizeText = (sz) => (sz ? sz + (S.data.sizeUnit ? '\u00a0' + S.data.sizeUnit : '') : '');
const unitNb = (t) => (t || '').replace(/ (kg|cm)$/, '\u00a0$1');

/** what the buttons would put into the cart right now */
function selection() {
  const d = S.data, o = d.offers[S.offer] || d.offers[d.initialOffer];
  if (o && o.kind === 'product') {
    return { id: o.variant, available: o.a, price: o.p, name: o.name, label: o.name + (o.set ? ' · ' + o.set : ''), set: true, box: o.box };
  }
  const v = variantFor(S.weight, S.size);
  const opts = [unitNb(S.weight), sizeText(S.size)].filter(Boolean).join(' · ') || (v ? v.t : '');
  return {
    id: v ? v.id : null, available: !!(v && v.a), price: v ? v.p : '', name: o ? o.name : '', size: sizeText(S.size),
    label: (o && o.name ? o.name + ' · ' : '') + opts, set: false, box: o ? o.box : ''
  };
}

/* chips: sold-out mark per chip follows the other option ("8 kg" is sold out in 155×220, maybe not in 135×200) */
function markChips(attr, pos, cur) {
  $$('[' + attr + ']').forEach((c) => {
    const val = c.getAttribute(attr), off = !availableWith(pos, val), label = c.closest('[data-soldout-label]');
    c.setAttribute('aria-checked', val === cur ? 'true' : 'false');
    c.classList.toggle('is-soldout', off);
    let small = c.querySelector('small');
    const word = label ? label.getAttribute('data-soldout-label') : '';
    if (off && word && !small) { small = document.createElement('small'); small.textContent = word; c.appendChild(small); }
    if (!off && small) small.remove();
  });
}

/* the line under the chips: weights missing in the chosen size, or "nothing available" */
function renderUnavail() {
  const d = S.data;
  $$('[data-mds-unavail]').forEach((line) => {
    const txt = $('[data-mds-unavail-text]', line), mail = $('[data-mds-notify]', line);
    const any = d.variants.some((v) => v.a);
    const miss = d.weightPos ? [...new Set(d.variants.map((v) => v.o[d.weightPos - 1]))].filter((w) => !availableWith(d.weightPos, w)) : [];
    if (!miss.length && any) { line.hidden = true; return; }
    const join = line.getAttribute('data-join') || '';
    let list = miss.length > 1 ? miss.slice(0, -1).join(', ') + ' ' + join + ' ' + miss[miss.length - 1] : (miss[0] || '');
    if (list && S.size && d.sizePos) list += ' (' + sizeText(S.size) + ')';
    if (txt) txt.textContent = any ? (line.getAttribute('data-prefix') || '') + ' ' + list : line.getAttribute('data-all');
    if (mail) mail.href = 'mailto:' + mail.getAttribute('data-email') + '?subject=' + encodeURIComponent((line.getAttribute('data-subject') || '').replace('[gewicht]', list || S.weight || ''));
    line.hidden = false;
  });
}

/* radiogroups (chips, offer cards, sticky weights): only the checked radio is in the tab order */
function rove() {
  $$('[role="radiogroup"]').forEach((g) => {
    const rs = $$('[role="radio"]', g);
    const on = rs.find((r) => r.getAttribute('aria-checked') === 'true') || rs[0];
    rs.forEach((r) => { r.tabIndex = r === on ? 0 : -1; });
  });
}

function render() {
  if (!S.data) return;
  const s = selection(), d = S.data;
  if (d.weightPos) markChips('data-mds-weight', d.weightPos, S.weight);
  if (d.sizePos) markChips('data-mds-size', d.sizePos, S.size);
  $$('[data-mds-offer]').forEach((c) => c.setAttribute('aria-checked', c.getAttribute('data-mds-offer') === S.offer ? 'true' : 'false'));
  $$('[data-mds-price]').forEach((n) => { n.textContent = s.price; });
  $$('[data-mds-variant-id]').forEach((n) => { n.value = s.id || ''; });
  const tv = variantFor(S.weight, S.size);
  if (tv) $$('[data-mds-this-price]').forEach((n) => { n.textContent = tv.p; });
  // the bar label breaks only after a " ·", never inside "Probeschlaf-Paket" or "7 kg" (desktop bar 1001-1180 px; review 16-10-26)
  $$('[data-mds-label]').forEach((n) => {
    const parts = s.label.split(' · ');
    n.replaceChildren(...parts.flatMap((t, i) => {
      const sp = document.createElement('span'); sp.className = 'mds-nw'; sp.textContent = t + (i < parts.length - 1 ? '\u00a0·' : '');
      return i ? [' ', sp] : [sp];
    }));
  });
  // sticky button's small line on phones: size + merchant text (with the offer name it was cut at 320-414 px; review 16-10-26)
  $$('[data-mds-sub]').forEach((n) => { n.textContent = [s.set ? s.name : s.size, n.getAttribute('data-sub')].filter(Boolean).join(' · '); });
  if (s.box) $$('[data-mds-box]').forEach((n) => { n.textContent = s.box; });
  const h = d.hints && d.hints[S.weight];
  $$('[data-mds-hint]').forEach((n) => {
    n.textContent = '';
    if (!h) return;
    const b = document.createElement('b'); b.textContent = h[0]; n.appendChild(b);
    if (h[1]) { const m = document.createElement('span'); m.textContent = ' · ' + h[1]; n.appendChild(m); }
  });
  $$('[data-weight-ui]').forEach((n) => { n.hidden = s.set || (n.hasAttribute('data-mds-sticky-weights') && !n.children.length); });
  $$('[data-set-ui]').forEach((n) => { n.hidden = !s.set; });
  $$('[data-mds-atc]').forEach((b) => {
    b.disabled = !s.available;
    b.classList.toggle('is-soldout', !s.available);
    const l = $('[data-mds-atc-label]', b);
    if (l && !b._mdsBusy) l.textContent = s.available ? l.getAttribute('data-label-add') : l.getAttribute('data-label-soldout');
  });
  $$('[data-mds-instock-only]').forEach((n) => { n.hidden = !s.available; });
  renderUnavail();
  rove();
}

function pick(attr, key, val) {
  if (!val || !S.data) return;
  if (!$$('[' + attr + ']').some((c) => c.getAttribute(attr) === val)) return;
  S[key] = val;
  const o = S.data.offers[S.offer];
  if (o && o.kind === 'product') S.offer = S.data.initialOffer;   // a single weight or size means a single blanket
  render();
}
const setWeight = (w) => pick('data-mds-weight', 'weight', w);
const setSize = (z) => pick('data-mds-size', 'size', z);
function setOffer(k) { if (S.data && S.data.offers[k]) { S.offer = k; render(); } }

function say(msg) {
  $$('[data-mds-atc-msg]').forEach((m) => { m.textContent = msg; });
  $$('[data-mds-toast]').forEach((t) => {
    t.textContent = msg; t.classList.add('is-on');
    clearTimeout(t._t); t._t = setTimeout(() => t.classList.remove('is-on'), 4000);
  });
}

async function addToCart(btn) {
  const d = S.data || { text: { added: '', addedShort: '', error: '' }, productTitle: '', productId: '' };
  const s = S.data ? selection() : { id: btn.getAttribute('data-variant'), available: !btn.disabled, label: '', price: '' };
  if (!s.id || !s.available || btn._mdsBusy) return;
  btn._mdsBusy = true;
  const label = $('[data-mds-atc-label]', btn);
  btn.setAttribute('aria-busy', 'true');
  const sections = $$('cart-items-component').map((c) => c.dataset.sectionId).filter(Boolean);
  let events = null;
  try { events = await import('@shopify/events'); } catch (e) { events = null; }
  const deferred = events ? events.CartLinesUpdateEvent.createPromise() : null;
  if (events) {
    btn.dispatchEvent(new events.CartLinesUpdateEvent({
      action: 'add', context: 'product', lines: [{ merchandiseId: String(s.id), quantity: 1 }], promise: deferred.promise
    }));
  }
  const routes = (typeof Theme !== 'undefined' && Theme.routes) || { cart_add_url: '/cart/add.js', cart_url: '/cart' };
  const fail = async (message) => {
    say(message || d.text.error);
    if (events) {
      events && btn.dispatchEvent(new events.CartErrorEvent({ error: message || 'Add to cart failed', code: 'INVALID' }));
      try {
        const cart = await (await fetch(routes.cart_url + '.js', { headers: { Accept: 'application/json' } })).json();
        deferred.resolve({ cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { didError: true, items: cart.items, source: 'mds-buy-box', itemCount: 1 } });
      } catch (e) { deferred.reject(e); }
    }
  };
  let ok = false;
  try {
    const res = await fetch(routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [{ id: s.id, quantity: 1 }], sections: sections.join(',') })
    });
    const json = await res.json();
    if (!res.ok || json.status) { await fail(json.description || json.message); return; }
    const cart = await (await fetch(routes.cart_url + '.js', { headers: { Accept: 'application/json' } })).json();
    if (events) {
      deferred.resolve({
        cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, sections: json.sections, didError: false, itemCount: 1, source: 'mds-buy-box', productId: String(d.productId) }
      });
    }
    say(d.text.added + ' ' + d.productTitle + ' · ' + s.label + ' · ' + s.price);
    if (label && btn.closest('[data-mds-sticky]')) {
      ok = true;   // the sticky button shows "Hinzugefügt" for 1.6 s, then frees itself
      label.textContent = d.text.addedShort;
      setTimeout(() => { btn._mdsBusy = false; render(); }, 1600);
    }
  } catch (e) {
    console.error('[mds-buy]', e);
    await fail(d.text.error);
  } finally {
    btn.removeAttribute('aria-busy');
    if (!ok) { btn._mdsBusy = false; render(); }   // after an error the button works again at once (review M1)
  }
}

/* gallery: swipe track + dots (phones), thumbnail rail + arrows (desktop) */
function initGallery(root) {
  const track = $('[data-gal-track]', root);
  if (!track || !claim(track, 'Gal')) return;
  const items = $$('.gal-item', track), dots = $('[data-gal-dots]', root), rail = $('[data-thumbs]', root);
  const go = (i) => track.scrollTo({ left: i * track.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
  const cur = () => Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
  items.forEach((it, i) => {
    const n = items.length;
    const d = document.createElement('button'); d.type = 'button'; d.setAttribute('aria-label', (root.dataset.galLabel || 'Bild') + ' ' + (i + 1) + ' / ' + n);
    d.addEventListener('click', () => go(i));
    if (dots) dots.appendChild(d);
    if (!rail) return;
    const t = document.createElement('button'); t.type = 'button'; t.setAttribute('aria-label', (root.dataset.galLabel || 'Bild') + ' ' + (i + 1) + ' / ' + n);
    const img = it.querySelector('img');
    if (img && img.getAttribute('src') && !it.dataset.thumbLabel) {
      const ti = document.createElement('img'); ti.loading = 'lazy'; ti.decoding = 'async'; ti.alt = '';
      ti.width = 76; ti.height = 76;
      // a 160 px Shopify image (data-thumb) or, for theme fallbacks, the smallest srcset entry
      ti.src = it.dataset.thumb || (img.getAttribute('srcset') || '').split(/\s/)[0] || img.getAttribute('src');
      t.appendChild(ti);
    } else {
      const l = document.createElement('span'); l.className = 't-lab'; l.textContent = it.dataset.thumbLabel || String(i + 1); t.appendChild(l);
    }
    t.addEventListener('click', () => go(i));
    rail.appendChild(t);
    it.addEventListener('mds:show', () => go(i));
  });
  const mark = () => {
    const i = cur();
    if (dots) $$('button', dots).forEach((d, k) => d.setAttribute('aria-current', k === i ? 'true' : 'false'));
    if (rail) $$('button', rail).forEach((d, k) => d.setAttribute('aria-current', k === i ? 'true' : 'false'));
  };
  track.addEventListener('scroll', () => requestAnimationFrame(mark), { passive: true });
  // films in the gallery play muted only while they are on screen (never with reduced motion: then they get controls)
  const vids = $$('video', track);
  if (vids.length && 'IntersectionObserver' in window) {
    if (reduce) vids.forEach((v) => { v.controls = true; });
    else {
      const vio = new IntersectionObserver((es) => es.forEach((e) => {
        const v = e.target;
        if (e.isIntersecting && e.intersectionRatio > 0.5) { v.muted = true; const pr = v.play(); if (pr && pr.catch) pr.catch(() => {}); } else v.pause();
      }), { threshold: [0, 0.5, 1] });
      vids.forEach((v) => vio.observe(v));
    }
  }
  $$('[data-gal-step]', root).forEach((b) => b.addEventListener('click', () => go(Math.min(items.length - 1, Math.max(0, cur() + Number(b.getAttribute('data-gal-step')))))));
  mark();
}

/* sticky bar: shown once [data-mds-sticky-start] has scrolled past (phones up to data-sticky-early px: from the start),
   hidden while any [data-hide-sticky] element is on screen and while a Horizon drawer is open (from 990 px the cart drawer
   is a side panel below the bar, whose bottom strip holds the subtotal and the checkout button; review 16-10-26) */
let stickyUpdate = null;
function initSticky() {
  const bar = $('[data-mds-sticky]');
  if (!bar || !claim(bar, 'Sticky')) return;
  // the bar's weight toggle copies the buy box chips (one source for the weights in stock)
  const holder = $('[data-mds-sticky-weights]', bar);
  if (holder && !holder.children.length) {
    $$('[data-mds-buy] [data-mds-weight]').forEach((c) => {
      const b = document.createElement('button');
      b.type = 'button'; b.setAttribute('role', 'radio'); b.setAttribute('aria-checked', c.getAttribute('aria-checked')); b.tabIndex = -1;
      b.setAttribute('data-mds-weight', c.getAttribute('data-mds-weight')); b.textContent = c.getAttribute('data-mds-weight');
      holder.appendChild(b);
    });
    if (!holder.children.length) holder.hidden = true;
  }
  const early = bar.getAttribute('data-sticky-early');
  const earlyMq = early && early !== '0' ? matchMedia('(max-width: ' + early + 'px)') : null;
  const seen = (el) => { const r = el.getBoundingClientRect(); return r.height > 0 && r.bottom > 0 && r.top < innerHeight; };
  stickyUpdate = () => {
    if (!bar.isConnected) return;
    const start = $('[data-mds-sticky-start]');
    const drawer = !!document.querySelector('theme-drawer[open]');
    const on = !drawer && ((earlyMq && earlyMq.matches) || !start || start.getBoundingClientRect().bottom < 0) && !$$('[data-hide-sticky]').some(seen);
    bar.classList.toggle('is-on', on);
    bar.classList.toggle('is-drawer', drawer);   // also hides the toast above the bar
    bar.setAttribute('aria-hidden', on ? 'false' : 'true');
    bar.inert = !on;
    document.documentElement.style.setProperty('--mds-sticky-h', on && getComputedStyle(bar).display !== 'none' ? bar.offsetHeight + 'px' : '0px');
  };
  let ticking = false;
  onScroll(() => { if (ticking) return; ticking = true; requestAnimationFrame(() => { ticking = false; stickyUpdate(); }); }, bar);
  stickyUpdate();
}

function init() {
  const data = readData();
  if (!data) { initSticky(); return; }
  // editor re-render of the same buy box keeps the current choice, unless a start setting (weight, size, offer) changed
  const prev = S.data;
  const same = prev && prev.sectionId === data.sectionId && prev.initialWeight === data.initialWeight &&
    prev.initialSize === data.initialSize && prev.initialOffer === data.initialOffer;
  S.data = data;
  if (!same) {
    S.weight = data.initialWeight;
    S.size = data.initialSize;
    S.offer = data.initialOffer;
  }
  $$('[data-mds-buy]').forEach(initGallery);
  initSticky();
  render();
}

if (!window.__mdsBuy) {
  window.__mdsBuy = true;
  document.addEventListener('click', (ev) => {
    const c = ev.target.closest && ev.target.closest('[data-mds-weight], [data-mds-size], [data-mds-offer], [data-mds-atc]');
    if (!c) return;
    if (c.hasAttribute('data-mds-atc')) { ev.preventDefault(); addToCart(c); return; }
    if (c.hasAttribute('data-mds-offer')) setOffer(c.getAttribute('data-mds-offer'));
    else if (c.hasAttribute('data-mds-size')) setSize(c.getAttribute('data-mds-size'));
    else setWeight(c.getAttribute('data-mds-weight'));
  });
  // arrow keys inside a radiogroup move and select (WAI-ARIA radio group pattern; review L6)
  document.addEventListener('keydown', (ev) => {
    const r = ev.target.closest && ev.target.closest('.mds [role="radio"]');
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[ev.key];
    if (!r || !step) return;
    const g = r.closest('[role="radiogroup"]');
    if (!g) return;
    const rs = $$('[role="radio"]', g), n = rs[(rs.indexOf(r) + step + rs.length) % rs.length];
    ev.preventDefault();
    n.click(); n.focus();
  });
  document.addEventListener('mds:pick', (e) => {
    const d = e.detail || {};
    if (d.weight) setWeight(/kg/.test(d.weight) ? d.weight : d.weight + ' kg');
    if (d.bundle) setOffer(d.bundle);
  });
  document.addEventListener('shopify:section:load', init);
  // Horizon drawers (cart, filters) fire these on document when they open or close. From 990 px an open drawer squeezes
  // .page-wrapper (margin transition), the buy box reflows and the buy button moves: check again once that layout settles
  // (next frames, and the end of the .page-wrapper transition), not only at the event (bar stayed hidden after close).
  const recheck = () => { if (stickyUpdate) stickyUpdate(); };
  ['theme-drawer:open', 'theme-drawer:close'].forEach((t) => document.addEventListener(t, () => {
    recheck();
    requestAnimationFrame(() => requestAnimationFrame(recheck));
  }));
  document.addEventListener('transitionend', (e) => { if (e.target.classList && e.target.classList.contains('page-wrapper')) recheck(); });
  document.addEventListener('shopify:block:select', (e) => {
    const slide = e.target && e.target.closest && e.target.closest('.gal-item');
    if (slide) slide.dispatchEvent(new CustomEvent('mds:show'));
  });
  window.MDS = { setWeight, setSize, setOffer, selection: () => (S.data ? selection() : null) };
  init();
}
